import { Link } from 'react-router-dom';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { veracityColors } from '../tokens';

// Daybreak mark native viewBox is 551.79 x 331.78 — width derives from height.
const MARK_ASPECT = 551.79 / 331.78;

export interface DaybreakMarkProps {
  /** Rendered height in px; width follows the mark's aspect ratio. */
  height?: number;
  color?: string;
  className?: string;
}

/**
 * DaybreakMark — the OneVeracity daybreak logo as an inline, recolorable SVG.
 * Same artwork as /daybreak-logo.svg in vera-shell/public.
 */
export function DaybreakMark({ height = 18, color = veracityColors.veracityOrange, className }: DaybreakMarkProps) {
  return (
    <svg
      width={height * MARK_ASPECT}
      height={height}
      viewBox="0 0 551.79 331.78"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <g fill={color} stroke={color} strokeMiterlimit={10}>
        <path d="M181.44,167.72c-2.24-2.71-4.72-5.2-7.41-7.45l-82.73-69.24c-1.6-1.34-3.98-1.13-5.31.47l-49.13,58.7c-1.34,1.6-1.13,3.98.47,5.31l95.01,79.52c5.03,4.21,2.04,12.39-4.51,12.38l-123.9-.23c-2.08,0-3.78,1.68-3.78,3.77L0,327.99c0,2.09,1.69,3.78,3.77,3.78h137.5c10.41.01,17.01-3.07,25.98-8.34l81.89-47.28c7.8-4.51,9.68-14.95,3.93-21.89l-71.64-86.54Z" />
        <path d="M551.65,250.95c0-2.08-1.7-3.77-3.78-3.77l-123.9.23c-6.55.01-9.54-8.18-4.51-12.38l95.01-79.52c1.6-1.34,1.81-3.72.47-5.31l-49.13-58.7c-1.34-1.6-3.72-1.81-5.31-.47l-82.73,69.24c-2.69,2.25-5.17,4.75-7.41,7.45l-71.64,86.54c-5.75,6.94-3.87,17.38,3.93,21.89l81.89,47.28c8.97,5.28,15.57,8.36,25.98,8.34h137.5c2.09,0,3.78-1.7,3.77-3.78l-.14-77.04Z" />
        <path d="M368.54,128.33l35.21-97.2c.71-1.96-.3-4.12-2.26-4.84L329.51.23c-1.96-.71-4.12.3-4.83,2.26l-42.19,116.49c-2.23,6.16-10.95,6.16-13.18,0L227.11,2.49c-.71-1.96-2.87-2.97-4.83-2.26l-71.97,26.07c-1.96.71-2.97,2.87-2.26,4.83l35.22,97.21c2.26,6.23,5.57,12.03,9.8,17.14l71.63,86.52c5.82,7.03,16.6,7.03,22.42,0l71.63-86.53c4.23-5.11,7.54-10.9,9.8-17.14Z" />
      </g>
    </svg>
  );
}

// Vera icon (TECH-26-003) native viewBox is 767.01 x 695.76 — width derives from height.
const VERA_ICON_ASPECT = 767.01 / 695.76;

/**
 * VeraIconMark — the Vera platform icon as an inline, recolorable SVG.
 * Same artwork as docs/design/TECH-26-003_Vera-App-Icon. First Light on dark
 * headers, Veracity Orange on light ones.
 */
export function VeraIconMark({ height = 18, color = veracityColors.veracityOrange, className }: DaybreakMarkProps) {
  return (
    <svg
      width={height * VERA_ICON_ASPECT}
      height={height}
      viewBox="0 0 767.01 695.76"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path
        fill={color}
        d="M667.48,0c-49.45,0-90.58,36.24-98.25,83.56-46.34-.04-108.81-.02-185.92.06-66.48.07-133.35.17-185.48.25C190.3,36.4,149.09,0,99.53,0,44.65,0,0,44.65,0,99.53s44.65,99.53,99.53,99.53c6.63,0,13.11-.66,19.38-1.9l189.29,334.04c-15.09,17.45-24.23,40.19-24.23,65.02,0,54.88,44.65,99.53,99.53,99.53s99.53-44.65,99.53-99.53c0-24.83-9.14-47.57-24.23-65.02l189.3-334.04c6.27,1.24,12.75,1.9,19.38,1.9,54.88,0,99.53-44.65,99.53-99.53S722.36,0,667.48,0ZM415.47,501.96c-10.04-3.41-20.79-5.26-31.96-5.26s-21.93,1.85-31.96,5.26L165.63,173.88c11.57-10.3,20.74-23.24,26.52-37.88,135.3-.23,287.97-.4,382.6-.32,5.78,14.77,14.99,27.83,26.64,38.2l-185.92,328.07Z"
      />
    </svg>
  );
}

const useStyles = makeStyles({
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    borderRadius: '4px',
    textDecoration: 'none',
    flexShrink: 0,
  },
  brandLight: {
    ':hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
  },
  brandDark: {
    ':hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
  },
});

export interface VeraBrandProps {
  /** Apply the on-dark treatment (First Light mark) for colored module headers. */
  onDark?: boolean;
  /** Daybreak mark height in px. */
  markHeight?: number;
  /** Override the mark color (e.g. Midnight Charcoal on light headers like Daybreak Amber). */
  markColor?: string;
  className?: string;
}

/**
 * VeraBrand — the Daybreak mark as a link back to the shell home page.
 * No wordmark: every module title already starts with "Vera". The mark is
 * First Light (near-white) on dark headers, brand orange on light ones.
 */
export function VeraBrand({ onDark, markHeight = 20, markColor, className }: VeraBrandProps) {
  const styles = useStyles();
  return (
    <Link
      to="/"
      className={mergeClasses(styles.brand, onDark ? styles.brandDark : styles.brandLight, className)}
      title="Vera home"
      aria-label="Vera home"
    >
      <VeraIconMark
        height={markHeight}
        color={markColor ?? (onDark ? veracityColors.firstLight : veracityColors.veracityOrange)}
      />
    </Link>
  );
}
